import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminKeys } from '@/hooks/api/query-keys';
import { fetchApi, buildQueryString, ApiClientError } from '@/lib/api/client';
import { API_CONFIG } from '@/lib/api/config';
import { ADMIN_TOKEN_KEY, handleAdmin401 } from '@/lib/admin/session/admin-session';
import type {
  AdminDashboardStats,
  AdminCompany,
  AdminCompanyDomain,
  AdminCompanyPurgePreview,
  AdminWorkspace,
  AdminUser,
  SuperAdmin,
  AdminMe,
  SuperAdminInviteResult,
  AdminAuditLog,
  AdminFeedbackTicket,
  AdminFeedbackStats,
  ErrorLogResponse,
  ErrorIssueListResponse,
  AsyncJob,
  AsyncJobsCountRow,
  AsyncJobsListFilter,
  PaginatedAsyncJobs,
  SystemEmailLog, SystemEmailLogCategoryCount } from '@/lib/types/admin';

// ============ Admin Auth Helpers ============

// The step-up rule and the ONE session handler live in lib/admin/session/admin-session.ts (console
// error pages, 2026-09-27); re-exported here, where the console has always imported them from.
export { STEP_UP_ERROR_CODES, isStepUpError, shouldClearAdminSession } from '@/lib/admin/session/admin-session';

/** The admin Bearer token — exported for multipart uploads that bypass fetchApi. */
export function getAdminToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

function getAdminHeaders(): Record<string, string> {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Thin wrapper around fetchApi for admin endpoints.
 * Uses skipAuth to prevent the standard app token / workspace header injection,
 * and instead attaches the admin Bearer token. A 401 that means the session is
 * over goes to the shared handler (the signed-out page); a step-up refusal is
 * rethrown for the dialog.
 */
export async function adminFetch<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  // During SSR, localStorage isn't available — return empty to let client hydrate
  if (typeof window === 'undefined') {
    return undefined as unknown as T;
  }

  try {
    return await fetchApi<T>(endpoint, {
      ...options,
      skipAuth: true,
      headers: {
        ...getAdminHeaders(),
        ...(options?.headers as Record<string, string>),
      },
    });
  } catch (error) {
    handleAdmin401(error);
  }
}

// ============ Auth Hooks ============

// Shape of the password-step response after 2FA enforcement.
// The backend NEVER returns accessToken at the password step — it always
// redirects to setup or verify.
//
// `requiresPasswordChange` is set when an INVITED super-admin (provisioned with
// a one-time temp password + mustChangePassword) signs in. The login page must
// route them to /admin/set-password BEFORE the 2FA setup/verify step.
export type AdminLoginResponse =
  | { requiresSetup: true; setupToken: string; requiresPasswordChange?: boolean }
  | {
      requiresTwoFactor: true;
      tempToken: string;
      requiresPasswordChange?: boolean;
    };

export function useAdminLogin() {
  return useMutation({
    mutationFn: async (data: {
      email: string;
      password: string;
    }): Promise<AdminLoginResponse> => {
      return fetchApi<AdminLoginResponse>('/auth/super-admin/login', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify(data),
      });
    },
    // Token saving happens on the verify/enable pages after the second factor
    // is confirmed — NOT here. The password step only produces a short-lived
    // bridge token, never a real session.
  });
}

/**
 * The signed-in admin's own profile (/auth/super-admin/me): name, 2FA, last sign-in.
 *
 * NOT what they may do: gate menus, pages and controls on useAdminAccess()
 * (lib/admin/hooks/use-admin-access.ts, GET /admin/me), which reads the role's
 * permissions from the table. The role and permissions this answer still carries
 * are kept for older consoles only.
 */
export function useAdminMe() {
  return useQuery({
    queryKey: ['admin', 'me'],
    queryFn: () => adminFetch<SuperAdmin>('/auth/super-admin/me'),
    enabled: !!getAdminToken(),
    retry: false,
    select: (me): AdminMe | undefined =>
      me && { ...me, role: me.role ?? 'super_admin', permissions: me.permissions ?? [] },
  });
}

// ============ Dashboard ============

export function useAdminDashboard() {
  return useQuery({
    queryKey: adminKeys.dashboard(),
    queryFn: () => adminFetch<AdminDashboardStats>('/admin/context/dashboard'),
  });
}

// ============ Companies ============

export function useAdminCompanies(filter?: Record<string, unknown>) {
  const params = { limit: 500, ...filter };
  return useQuery({
    queryKey: adminKeys.companies(params),
    queryFn: async () => {
      const qs = buildQueryString(params);
      const res = await adminFetch<{ data: AdminCompany[]; total: number }>(`/admin/companies${qs}`);
      return res?.data ?? res ?? [];
    },
    select: (data) => (Array.isArray(data) ? data : []) as AdminCompany[],
  });
}

/**
 * Company detail — GET /admin/companies/:id.
 *
 * The detail response is the AdminCompany list shape EXTENDED with nested
 * relations (workspaces / domains / subscription / entitlements). All of those
 * are optional on AdminCompany so consumers can read them defensively.
 */
export function useAdminCompany(id: string | null | undefined) {
  return useQuery({
    queryKey: adminKeys.company(id ?? ''),
    queryFn: () => adminFetch<AdminCompany>(`/admin/companies/${id}`),
    enabled: !!id,
  });
}

export function useCreateAdminCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      // Optional: the backend derives + de-duplicates the slug from the name
      // when omitted. Slug generation is authoritative on the backend.
      slug?: string;
      description?: string;
      website?: string;
      address?: string;
      industry?: string;
      companySize?: string;
      country?: string;
      timezone?: string;
      // The company's own domain: REQUIRED. It is its first domain, and its brand is found from it.
      domain: string;
      // Set up the brand found for the domain before the owner first signs in. The API's default is true.
      applyBrand?: boolean;
      // Admin-only controls accepted by the BE create DTO and passed straight
      // through by adminFetch (which JSON-stringifies the whole body).
      tier?: string;
      billingEmail?: string;
      seatLimit?: number;
      recipientLimit?: number;
      workspaceLimit?: number;
      // Owner-bootstrap fields. `ownerEmail` is now REQUIRED by the BE create
      // DTO — it provisions the owner user and the response includes one-time
      // { tempPassword, loginUrl, ownerEmail } (already typed on AdminCompany).
      // The create drawer validates non-empty before submit. First/last stay
      // optional.
      ownerEmail: string;
      ownerFirstName?: string;
      ownerLastName?: string;
      // When true, the BE emails the owner their one-time credentials; when
      // false/omitted, the operator delivers them from the reveal dialog.
      sendCredentialsEmail?: boolean;
    }) =>
      adminFetch<AdminCompany>('/admin/companies', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      // Invalidate all admin queries — query keys include filter params (e.g. limit:50)
      // so we must use the top-level key to match all variants
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export function useUpdateAdminCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AdminCompany> }) =>
      adminFetch<AdminCompany>(`/admin/companies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export function useToggleCompanyStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminFetch<AdminCompany>(`/admin/companies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Preview what a permanent company delete will remove.
 * GET /admin/companies/:id/purge-preview — includes already-soft-deleted companies.
 */
export function useAdminCompanyPurgePreview(id: string | null | undefined) {
  return useQuery({
    queryKey: [...adminKeys.all, 'companies', 'purge-preview', id] as const,
    queryFn: () =>
      adminFetch<AdminCompanyPurgePreview>(`/admin/companies/${id}/purge-preview`),
    enabled: !!id,
  });
}

/**
 * Permanently delete a company — POST /admin/companies/:id/purge { password }.
 *
 * Requires the signed-in super-admin password. Hard-deletes the company row
 * (CASCADE + leftover user rows). Invalidates the admin tree so the list and
 * dashboard counts refresh.
 */
export function useDeleteAdminCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      adminFetch<{ id: string; deleted: true; name: string }>(
        `/admin/companies/${id}/purge`,
        {
          method: 'POST',
          body: JSON.stringify({ password }),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Repair a broken / piecemeal tenant — POST /admin/companies/:id/repair.
 *
 * Heals a company whose bootstrap was left half-done (missing user_workspaces
 * membership, missing per-company defaults, stuck status). The BE re-creates
 * the missing memberships + all defaults, flips status → Active, and runs
 * onboarding. Body { renameWorkspaceToMain?: boolean } optionally renames the
 * tenant's single workspace to "Main". Returns a shape-count summary which the
 * caller surfaces in a toast. Invalidates the whole admin tree so the company
 * detail + list refresh.
 */
export function useRepairAdminCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      renameWorkspaceToMain,
    }: {
      id: string;
      renameWorkspaceToMain?: boolean;
    }) =>
      adminFetch<{
        companyId: string;
        status: string | null;
        onboardingCompletedAt: string | null;
        counts: Record<string, number>;
      }>(`/admin/companies/${id}/repair`, {
        method: 'POST',
        body: JSON.stringify({ renameWorkspaceToMain }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Upload a company brand logo — POST /admin/companies/:id/logo.
 *
 * The cropped image (already a PNG produced client-side by the crop modal) is
 * sent as multipart/form-data under the field name `file`. We can't route this
 * through `adminFetch` / `fetchApi` because those force a JSON Content-Type,
 * which breaks the multipart boundary — so we hit the BE directly with a raw
 * fetch carrying the admin Bearer token. Returns { logoUrl } (a relative
 * `/uploads/...` path). Invalidates the admin tree so the list + detail refresh.
 */
export function useUploadCompanyLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: Blob }) => {
      const token = getAdminToken();
      const form = new FormData();
      // Give the blob a filename so multer accepts it as a file part.
      form.append('file', file, 'logo.png');
      const res = await fetch(
        `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/admin/companies/${id}/logo`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: form,
        },
      );
      if (!res.ok) {
        let message = `Logo upload failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.message) {
            message = Array.isArray(body.message)
              ? body.message.join('. ')
              : String(body.message);
          }
        } catch {
          // non-JSON error body — keep the default message
        }
        throw new ApiClientError(res.status, res.statusText, { message });
      }
      return (await res.json()) as { logoUrl: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Remove a company brand logo — DELETE /admin/companies/:id/logo.
 *
 * Nulls companies.logo_url on the BE. Returns { logoUrl: null }. Invalidates
 * the admin tree so the list + detail refresh.
 */
export function useRemoveCompanyLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ logoUrl: null }>(`/admin/companies/${id}/logo`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Add a domain to a company — POST /admin/companies/:id/domains.
 *
 * Body: { name, domain, workspaceId? }. When workspaceId is omitted the domain
 * is attached at the company level (workspace_id = NULL). Returns the created
 * AdminCompanyDomain row (matching the nested shape on the company-detail
 * response). Invalidates the whole admin tree so the company-detail Domains tab
 * and any list views refresh.
 */
export function useAddCompanyDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { name: string; domain: string; workspaceId?: string };
    }) =>
      adminFetch<AdminCompanyDomain>(`/admin/companies/${id}/domains`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Remove a domain from a company — DELETE /admin/companies/:id/domains/:domainId.
 *
 * The BE soft-deletes the domain row. Invalidates the whole admin tree so the
 * company-detail Domains tab refreshes immediately.
 */
export function useDeleteCompanyDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, domainId }: { id: string; domainId: string }) =>
      adminFetch<{ id: string; deleted: true }>(
        `/admin/companies/${id}/domains/${domainId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

// ============ Workspaces ============

export function useAdminWorkspaces(
  filter?: Record<string, unknown>,
  options: { enabled?: boolean } = {},
) {
  const params = { limit: 500, ...filter };
  return useQuery({
    queryKey: adminKeys.workspaces(params),
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const qs = buildQueryString(params);
      const res = await adminFetch<{ data: AdminWorkspace[]; total: number }>(`/admin/workspaces${qs}`);
      return res?.data ?? res ?? [];
    },
    select: (data) => (Array.isArray(data) ? data : []) as AdminWorkspace[],
  });
}

/**
 * Workspace detail — GET /admin/workspaces/:id.
 *
 * No dedicated single-workspace key exists on `adminKeys` (the factory only has
 * the list-level `workspaces` node), so we derive a stable detail key inline
 * following the same `[...all, resource, 'detail', id]` shape used by
 * adminKeys.asyncJob / adminKeys.sendingDomain.
 */
export function useAdminWorkspace(id: string | null | undefined) {
  return useQuery({
    queryKey: [...adminKeys.all, 'workspaces', 'detail', id ?? ''] as const,
    queryFn: () => adminFetch<AdminWorkspace>(`/admin/workspaces/${id}`),
    enabled: !!id,
  });
}

export function useCreateAdminWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      companyId: string;
      slug?: string;
      industry?: string;
      country?: string;
      timezone?: string;
      language?: string;
      status?: string;
      dateFormat?: string;
      useCompanyBranding?: boolean;
      customEmailHeader?: boolean;
    }) =>
      adminFetch<AdminWorkspace>('/admin/workspaces', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Update a workspace — PATCH /admin/workspaces/:id.
 *
 * Body mirrors UpdateAdminWorkspaceDto on the BE: { name?, timezone?,
 * language?, status?, settings? }. The BE writes `language` → the `locale`
 * column and `status` straight to the workspace_status enum (Active /
 * Suspended / Archived). Invalidates the whole admin tree so the workspace
 * detail, the company-detail Workspaces tab, and any list views refresh.
 */
export function useUpdateAdminWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{
        name: string;
        /** ISO 3166-1 alpha-2; null clears it. Moves no zone (country-first tenant creation). */
        country: string | null;
        timezone: string;
        language: string;
        status: string;
        // Workspace settings merged into workspaces.settings by the BE — same
        // storage the customer Workspace Settings page writes.
        dateFormat: string;
        useCompanyBranding: boolean;
        customEmailHeader: boolean;
        settings: Record<string, unknown>;
      }>;
    }) =>
      adminFetch<AdminWorkspace>(`/admin/workspaces/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * The company drawer's country change, applied to the ticked workspaces: ONE
 * request, so one row in the admin audit log (#1917).
 */
export function useBulkAdminWorkspaceCountry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { companyId: string; ids: string[]; country: string }) =>
      adminFetch<{ updated: number; ids: string[]; notUpdated: string[] }>('/admin/workspaces/bulk-country', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Archive a workspace — DELETE /admin/workspaces/:id.
 *
 * The BE soft-deletes the row (`deleted_at` + `status='Archived'`) and REFUSES
 * with a 400 while the workspace still holds live recipients or campaigns, or
 * when it is the company's last live workspace. The message names the blockers
 * and their counts, so callers should surface `error.message` verbatim rather
 * than a generic string.
 *
 * Invalidates the whole admin tree so the workspaces list, the company-detail
 * Workspaces tab and the workspace detail screen all refresh.
 */
export function useDeleteAdminWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ id: string }>(`/admin/workspaces/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

// ============ Users ============

export function useAdminUsers(filter?: Record<string, unknown>) {
  const params = { limit: 500, ...filter };
  return useQuery({
    queryKey: adminKeys.users(params),
    queryFn: async () => {
      const qs = buildQueryString(params);
      const res = await adminFetch<{ data: AdminUser[]; total: number }>(`/admin/users${qs}`);
      return res?.data ?? res ?? [];
    },
    select: (data) => ((Array.isArray(data) ? data : []) as AdminUser[]).map((user) => ({
      ...user,
      companyMemberships: user.companyMemberships ?? [],
    })),
  });
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    // `workspaceId` is now REQUIRED by the BE (POST /admin/users) — it creates
    // the user_workspaces membership + company_users row from it. For the FIRST
    // user of a company the role must be 'Company Owner'.
    mutationFn: (data: {
      email: string;
      companyId: string;
      workspaceId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
      /** Opt-in Welcome email (login link only — never the password). Default false. */
      sendWelcomeEmail?: boolean;
    }) =>
      adminFetch<AdminUser>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Reset a user's password — POST /admin/users/:id/reset-password.
 *
 * The BE mints a NEW one-time temp password, forces a reset on next login
 * (mustChangePassword), and revokes all of the user's live sessions (instant
 * logout). The response carries the one-time { tempPassword, loginUrl, email }
 * which the caller surfaces in a shown-once reveal dialog — never re-fetchable.
 */
export function useResetAdminUserPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ id: string; email: string; tempPassword: string; loginUrl: string }>(
        `/admin/users/${id}/reset-password`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Enable / disable a user — PATCH /admin/users/:id { isActive }.
 *
 * Setting isActive=false revokes every live session on the BE; the JWT strategy
 * re-reads the user each request and rejects when !isActive, so the user is
 * logged out on their next request. Re-enabling just flips the flag back.
 */
export function useToggleAdminUserActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminFetch<AdminUser>(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Rename a user. `PATCH /admin/users/:id` accepted these fields from the day it
 * was written and had NO caller until 2026-09-17 — the admin area offered no
 * way to fix a name anywhere. Email and role are deliberately not sent: the
 * endpoint does not accept them.
 */
export function useUpdateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string;
      firstName?: string;
      lastName?: string;
    }) =>
      adminFetch<AdminUser>(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

// ============ Super Admins ============

export function useAdminSuperAdmins(filter?: Record<string, unknown>) {
  const params = { limit: 50, ...filter };
  return useQuery({
    queryKey: adminKeys.superAdmins(params),
    queryFn: async () => {
      const qs = buildQueryString(params);
      return adminFetch<SuperAdmin[]>(`/admin/super-admins${qs}`);
    },
    select: (data) => data ?? [],
  });
}

export function useCreateSuperAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; password: string; firstName?: string; lastName?: string }) =>
      adminFetch<SuperAdmin>('/admin/super-admins', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Invite a new super-admin — POST /admin/super-admins (invite mode).
 *
 * Omitting `password` puts the BE into invite mode: it mints a one-time temp
 * password, flags the account mustChangePassword, and emails an AdminInviteEmail
 * with the login URL. The response carries { invited, emailSent, tempPassword? }
 * exactly once — the temp password is an operator fallback when email delivery
 * is unavailable and is surfaced in a shown-once copyable reveal box.
 */
export function useInviteSuperAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      email: string;
      firstName?: string;
      lastName?: string;
      /** A role's key from GET /admin/roles — required: nobody becomes a Super Admin by default. */
      role: string;
    }) =>
      adminFetch<SuperAdminInviteResult>('/admin/super-admins', {
        method: 'POST',
        body: JSON.stringify({ ...data, invite: true }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * First-login password set for an INVITED super-admin —
 * POST /admin/super-admins/set-password (public, authenticated by the setup
 * token). No current password is required because the account was provisioned
 * with a temp password and flagged mustChangePassword. Returns { message }.
 */
export function useSetSuperAdminPassword() {
  return useMutation({
    mutationFn: (data: { setupToken: string; newPassword: string }) =>
      fetchApi<{ message: string }>('/admin/super-admins/set-password', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify(data),
      }),
  });
}

/**
 * Update a super admin — PATCH /admin/super-admins/:id.
 *
 * Body mirrors UpdateSuperAdminDto on the BE: { firstName?, lastName?,
 * displayName?, isActive? }. The common use is toggling isActive (deactivate /
 * reactivate). The BE refuses to let a super-admin deactivate themselves.
 */
export function useUpdateSuperAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{
        firstName: string;
        lastName: string;
        displayName: string;
        isActive: boolean;
        // A role's key from GET /admin/roles. Refused by the API for the
        // primary and for your own account.
        role: string;
      }>;
    }) =>
      adminFetch<SuperAdmin>(`/admin/super-admins/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Soft-delete a super admin — DELETE /admin/super-admins/:id.
 *
 * The BE soft-deletes and audit-logs the action; it refuses to let a super-admin
 * delete themselves. Invalidates the whole admin tree so the super-admins list
 * refreshes.
 */
export function useDeleteSuperAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ success: boolean }>(`/admin/super-admins/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

// ============ Audit Logs ============

export function useAdminAuditLogs(filter?: Record<string, unknown>) {
  const params = { limit: 500, ...filter };
  return useQuery({
    queryKey: adminKeys.auditLogs(params),
    queryFn: async () => {
      const qs = buildQueryString(params);
      const res = await adminFetch<{ data: AdminAuditLog[]; total: number }>(`/admin/audit-logs${qs}`);
      return res?.data ?? res ?? [];
    },
    select: (data) => (Array.isArray(data) ? data : []) as AdminAuditLog[],
  });
}

// ============ System Email Logs (internal/platform mail ledger) ============

/**
 * The internal mail ledger. Separate from /email-logs because that endpoint is
 * workspace-scoped and these rows include mail with no tenant at all, so serving
 * them there would show one customer another customer's platform mail.
 */
export function useAdminSystemEmailLogs(filter?: Record<string, unknown>) {
  const params = { limit: 100, page: 1, ...filter };
  return useQuery({
    queryKey: adminKeys.systemEmailLogs(params),
    queryFn: async () => {
      const qs = buildQueryString(params);
      return adminFetch<{ data: SystemEmailLog[]; total: number; page: number; limit: number }>(
        `/admin/system-email-logs${qs}`,
      );
    },
  });
}

/** Facet counts, aggregated in SQL — the browser never counts rows itself. */
export function useAdminSystemEmailLogCounts(filter?: Record<string, unknown>) {
  return useQuery({
    queryKey: adminKeys.systemEmailLogCounts(filter ?? {}),
    queryFn: async () => {
      const qs = buildQueryString(filter ?? {});
      const res = await adminFetch<SystemEmailLogCategoryCount[]>(
        `/admin/system-email-logs/category-counts${qs}`,
      );
      return Array.isArray(res) ? res : [];
    },
  });
}

// ============ System Logs (Error Journal) ============

export function useAdminSystemLogs(filter: { date: string; type?: string; source?: string; module?: string; search?: string; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: adminKeys.systemLogs(filter),
    queryFn: async () => {
      const qs = buildQueryString(filter);
      return adminFetch<ErrorLogResponse>(`/admin/error-logs${qs}`);
    },
    enabled: !!filter.date,
  });
}

// ============ Error Issues (Sentry-like tracking) ============

export function useAdminErrorIssues(filter?: Record<string, unknown>) {
  const params = { limit: 50, sortBy: 'lastSeenAt', sortOrder: 'DESC', ...filter };
  return useQuery({
    queryKey: adminKeys.errorIssues(params),
    queryFn: async () => {
      const qs = buildQueryString(params);
      return adminFetch<ErrorIssueListResponse>(`/admin/error-issues${qs}`);
    },
  });
}

export function useUpdateIssueStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'Fixed' | 'Open' }) => {
      return adminFetch(`/admin/error-issues/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
        headers: { 'Content-Type': 'application/json' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.errorIssues() });
    },
  });
}

export function useBulkIssueAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, action }: { ids: string[]; action: 'Fix' | 'Reopen' }) => {
      return adminFetch('/admin/error-issues/bulk-action', {
        method: 'POST',
        body: JSON.stringify({ ids, action }),
        headers: { 'Content-Type': 'application/json' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.errorIssues() });
    },
  });
}

// ============ Feedback (cross-workspace) ============

export function useAdminFeedback(filter?: Record<string, unknown>) {
  const params = { limit: 500, ...filter };
  return useQuery({
    queryKey: adminKeys.feedback(params),
    queryFn: async () => {
      const qs = buildQueryString(params);
      const res = await adminFetch<{ data: AdminFeedbackTicket[]; total: number }>(`/admin/feedback${qs}`);
      return res?.data ?? res ?? [];
    },
    select: (data) => (Array.isArray(data) ? data : []) as AdminFeedbackTicket[],
  });
}

export function useAdminFeedbackTicket(id: string | undefined) {
  return useQuery({
    queryKey: [...adminKeys.all, 'feedback', id],
    queryFn: () => adminFetch<AdminFeedbackTicket & {
      userStory?: string | null
      acceptanceCriteria?: string[] | null
      affectedArea?: string | null
      url?: string | null
      pathname?: string | null
      userAgent?: string | null
      screenResolution?: string | null
      adminNotes?: string | null
      aiGenerated?: boolean
      // Feedback Desk (2026-09-02)
      resolutionNote?: string | null
      fixRef?: string | null
      deployEnv?: string | null
      resolvedAt?: string | null
      resolvedByName?: string | null
      verifiedAt?: string | null
      verifiedByName?: string | null
      threadMessages?: Array<{ id: string; role: string; senderName: string; message: string; createdAt: string }>
      screenshots?: Array<{ id: string; type?: string; fileKey?: string; fileUrl?: string }>
      statusHistory?: Array<{ id: string; fromStatus: string; toStatus: string; createdAt: string }>
    }>(`/admin/feedback/${id}`),
    enabled: !!id,
  });
}

/**
 * Update an admin feedback ticket.
 *
 * Evidence-required-at-Resolve fields (added 2026-05-11):
 *   - fixCommitSha + prodDeployId  → real-fix evidence (BOTH required)
 *   - pipelineState                → closure-without-code whitelist value
 *                                     ('Already Fixed' | "Won't Fix" |
 *                                     'Duplicate' | 'Needs Data')
 *   - reopenReason                 → required when flipping Resolved → Open
 *
 * Backed by the DB CHECK constraint chk_resolved_requires_evidence — the
 * BE will return a 400 if neither evidence pair nor closure-state is set
 * on a Resolved transition.
 */
export type UpdateAdminFeedbackPatch = Partial<{
  status: string;
  priority: string;
  category: string;
  adminNotes: string;
  affectedArea: string;
  fixCommitSha: string;
  prodDeployId: string;
  fixBranch: string;
  pipelineState: string;
  reopenReason: string;
  // Feedback Desk (2026-09-02): required (>= 10 chars) when the status
  // moves to Resolved / Closed / Wont Fix; the API refuses otherwise.
  resolutionNote: string;
  fixRef: string;
  deployEnv: 'staging' | 'production' | 'none';
}>;

export function useUpdateAdminFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdminFeedbackPatch }) =>
      adminFetch<AdminFeedbackTicket>(`/admin/feedback/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'feedback'] });
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'feedback', id] });
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'feedback', id, 'timeline'] });
      queryClient.invalidateQueries({ queryKey: adminKeys.feedbackStats() });
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'feedback', 'repeated-failures'] });
    },
  });
}

// ─── Timeline tab — chronological status + pipeline events ─────────────────

export type AdminFeedbackTimelineEntry =
  | {
      kind: 'status';
      id: string;
      at: string;
      fromStatus: string;
      toStatus: string;
      actorId: string | null;
      actorEmail: string | null;
      actorName: string | null;
    }
  | {
      kind: 'pipeline';
      id: string;
      at: string;
      fromStage: string | null;
      toStage: string;
      fromState: string | null;
      toState: string;
      actor: string;
      actorId: string | null;
      metadata: Record<string, unknown> | null;
    };

export function useAdminFeedbackTimeline(ticketId: string | null | undefined) {
  return useQuery({
    queryKey: [...adminKeys.all, 'feedback', ticketId, 'timeline'],
    queryFn: () =>
      adminFetch<{ ticketId: string; entries: AdminFeedbackTimelineEntry[] }>(
        `/admin/feedback/${ticketId}/timeline`,
      ),
    enabled: !!ticketId,
  });
}

// ─── Tester verification gate ───────────────────────────────────────────────

/** Verify on a selection: one request, so one row in the admin audit log (#1917). */
export function useBulkVerifyAdminFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      adminFetch<{ affected: number; ids: string[]; notVerified: { id: string; errorCode: string }[] }>(
        '/admin/feedback/bulk-verify',
        { method: 'POST', body: JSON.stringify({ ids }) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'feedback'] });
    },
  });
}

export function useVerifyAdminFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<AdminFeedbackTicket>(`/admin/feedback/${id}/verify`, {
        method: 'POST',
      }),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'feedback'] });
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'feedback', id] });
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'feedback', id, 'timeline'] });
    },
  });
}

// ─── Repeated failures dashboard ────────────────────────────────────────────

export function useAdminFeedbackRepeatedFailures(filters?: {
  page?: number;
  limit?: number;
  minCount?: number;
}) {
  const params = { page: 1, limit: 50, minCount: 2, ...filters };
  return useQuery({
    queryKey: [...adminKeys.all, 'feedback', 'repeated-failures', params],
    queryFn: async () => {
      const qs = buildQueryString(params);
      return adminFetch<{
        data: Array<AdminFeedbackTicket & {
          reopenCount: number;
          lastReopenedAt: string | null;
          lastReopenReason: string | null;
        }>;
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }>(`/admin/feedback/repeated-failures${qs}`);
    },
  });
}

export function useGenerateAdminDevSpec() {
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ content: string; ticketId: string }>(`/admin/feedback/${id}/dev-spec`, {
        method: 'POST',
      }),
  });
}

export function useAdminFeedbackStats() {
  return useQuery({
    queryKey: adminKeys.feedbackStats(),
    queryFn: () => adminFetch<AdminFeedbackStats>('/admin/feedback/stats'),
  });
}

export function useUploadAdminScreenshot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, dataUrl, type }: { ticketId: string; dataUrl: string; type?: 'Auto' | 'Uploaded' }) =>
      adminFetch<{ id: string; ticketId: string; type: string; fileKey: string; fileUrl: string | null; createdAt: string }>(
        `/admin/feedback/${ticketId}/screenshots/upload`,
        {
          method: 'POST',
          body: JSON.stringify({ dataUrl, type: type ?? 'Uploaded' }),
        },
      ),
    onSuccess: (_data, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'feedback', ticketId] });
    },
  });
}

export function useDeleteAdminScreenshot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, screenshotId }: { ticketId: string; screenshotId: string }) =>
      adminFetch<{ id: string }>(`/admin/feedback/${ticketId}/screenshots/${screenshotId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'feedback', ticketId] });
    },
  });
}

// ============ Impersonation ============

export function useImpersonate() {
  return useMutation({
    mutationFn: (data: { userId: string; companyId: string; workspaceId?: string }) =>
      adminFetch<{ accessToken: string; refreshToken: string; redirectUrl: string }>(
        '/admin/impersonation/generate',
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
      ),
  });
}

// ============ Async Jobs (Phase B Wave 1) ============

export function useAdminAsyncJobs(filter?: AsyncJobsListFilter) {
  const params = { limit: 50, ...filter } as Record<string, unknown>;
  return useQuery({
    queryKey: adminKeys.asyncJobs(params),
    queryFn: async () => {
      const qs = buildQueryString(params);
      return adminFetch<PaginatedAsyncJobs>(`/admin/async-jobs${qs}`);
    },
    // Smooth pagination — keep the prior page visible during refetch.
    placeholderData: (prev) => prev,
  });
}

export function useAdminAsyncJob(id: string | null | undefined) {
  return useQuery({
    queryKey: adminKeys.asyncJob(id ?? ''),
    queryFn: () => adminFetch<AsyncJob>(`/admin/async-jobs/${id}`),
    enabled: !!id,
  });
}

export function useAdminAsyncJobsCounts() {
  return useQuery({
    queryKey: adminKeys.asyncJobsCounts(),
    queryFn: () => adminFetch<AsyncJobsCountRow[]>('/admin/async-jobs/counts'),
    // Refresh the overview banner every 30s while the page is open.
    refetchInterval: 30_000,
  });
}

// ============ Sending Domains (PR-3) ============

export interface AdminSendingDomain {
  id: string;
  domainName: string;
  scope: 'Global' | 'Company' | 'Workspace';
  isSystemDomain: boolean;
  purpose: 'Email' | 'Landing Pages' | 'Both';
  emailType: 'Phishing' | 'Operational';
  validationStatus: 'Pending' | 'Validating' | 'Valid' | 'Invalid' | 'Expired';
  ownershipVerified: boolean;
  sslGenerated: boolean;
  spfValid: boolean;
  dkimValid: boolean;
  dmarcValid: boolean;
  spfRecord: string | null;
  dkimRecord: string | null;
  dmarcRecord: string | null;
  espDomainId: string | null;
  lastValidatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Present on add/reprovision responses
  provisioningResult?: {
    success: boolean;
    validationStatus: string;
    alreadyProvisioned?: boolean;
    error?: string;
  };
}

export interface PaginatedAdminSendingDomains {
  data: AdminSendingDomain[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function useAdminSendingDomains(filter?: Record<string, unknown>) {
  const params = { limit: 50, ...filter } as Record<string, unknown>;
  return useQuery({
    queryKey: adminKeys.sendingDomains(params),
    queryFn: async () => {
      const qs = buildQueryString(params);
      return adminFetch<PaginatedAdminSendingDomains>(
        `/admin/sending-domains${qs}`,
      );
    },
    placeholderData: (prev) => prev,
  });
}

export function useAdminSendingDomain(id: string | null | undefined) {
  return useQuery({
    queryKey: adminKeys.sendingDomain(id ?? ''),
    queryFn: () => adminFetch<AdminSendingDomain>(`/admin/sending-domains/${id}`),
    enabled: !!id,
  });
}

export function useAddGlobalSendingDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      domainName: string;
      purpose: 'Email' | 'Landing Pages' | 'Both';
      emailType?: 'Phishing' | 'Operational';
    }) =>
      adminFetch<AdminSendingDomain>('/admin/sending-domains', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export function useReprovisionSendingDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<AdminSendingDomain>(
        `/admin/sending-domains/${id}/reprovision`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export function useDeleteAdminSendingDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ id: string; deleted: true }>(
        `/admin/sending-domains/${id}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}
